import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom'
import { Grid, TextField, RadioGroup, FormControlLabel, Radio, FormLabel, FormControl, CardHeader, CardContent, Divider, Card, Tooltip, IconButton, Container, Button, LinearProgress, Box, Chip, Typography } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {
  fetchInvoiceSettings,
  resetInvoiceNotifications,
  updateInvoiceSettings
} from './invoiceSlice';
import Notification from '../../../pages/General/Notification';

const InvoiceSettingsPage = () => {
  const dispatch = useDispatch();
  const profile = useSelector(state => state.auth.profile);
  const invoice = useSelector(state => state.invoice.invoiceSettings);
  const [invoiceType, setInvoiceType] = React.useState('standard');
  const [invoiceEmails, setInvoiceEmails] = React.useState([]);
  const [invoiceCCEmails, setInvoiceCCEmails] = React.useState([]);
  const [inputValue, setInputValue] = React.useState('');
  const [inputCCValue, setInputCCValue] = React.useState('');
  const [errors, setErrors] = React.useState({});

  const handleInvoiceTypeChange = (event) => {
    setInvoiceType(event.target.value);
  };

  const handleKeyDown = (event, type) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      const email = event.target.value.trim();
      if (email && validateEmail(email)) {
        if (type === 'invoice' && !invoiceEmails.includes(email) && invoiceEmails.length < 10) {
          setInvoiceEmails([...invoiceEmails, email]);
          setInputValue('');
          if (errors.invoiceEmail) {
            setErrors((prevErrors) => ({ ...prevErrors, invoiceEmail: null }));
          }
        } else if (type === 'invoiceCC' && !invoiceCCEmails.includes(email) && invoiceCCEmails.length < 10) {
          setInvoiceCCEmails([...invoiceCCEmails, email]);
          setInputCCValue('');
          if (errors.invoiceCCEmail) {
            setErrors((prevErrors) => ({ ...prevErrors, invoiceCCEmail: null }));
          }
        }
      }
    }
  };

  const handleDelete = (email, type) => {
    if (type === 'invoice') {
      setInvoiceEmails(invoiceEmails.filter((e) => e !== email));
    } else {
      setInvoiceCCEmails(invoiceCCEmails.filter((e) => e !== email));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
    return emailRegex.test(email);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const validationErrors = {};
    if (invoiceEmails.length === 0) {
      validationErrors.invoiceEmail = 'Invoice Email is required';
    }

    if (Object.values(validationErrors).some((error) => error)) {
      setErrors(validationErrors);
    } else {
      const formData = {
        invoice_email: invoiceEmails.join(','),
        invoice_cc_email: invoiceCCEmails.join(','),
        invoice_csv_type: invoiceType
      };
      dispatch(updateInvoiceSettings(profile && profile.id, formData))
    }
  };

  useEffect(() => {
    if (invoice && invoice.data) {
      setInvoiceEmails(invoice.data.invoice_email ? invoice.data.invoice_email.split(',') : []);
      setInvoiceCCEmails(invoice.data.invoice_cc_email ? invoice.data.invoice_cc_email.split(',') : []);
      setInvoiceType(invoice.data.invoice_csv_type || 'standard');
    }
  }, [invoice])

  useEffect(() => {
    dispatch(fetchInvoiceSettings())
  }, [])

  return (<>
    {(invoice && invoice.notification && invoice.notification.messageContent != '') &&
      <Notification
        is_open={invoice.notification.show}
        messageType={invoice.notification.messageType}
        messages={invoice.notification.messageContent}
        handleClose={() => dispatch(resetInvoiceNotifications())}
      />}
    <Card sx={{ minWidth: 1000, boxShadow: 'unset' }} className="sc--cardhead--details">
      <CardHeader title='Invoice Settings' className="sc--cardhead--details" action={
        <div>
          <Tooltip title="Back" placement="top-start">
            <Link to="/settings/billing">
              <IconButton style={{ position: "relative", left: '-20px' }}>
                <ArrowBackIosNewIcon style={{ color: "#1890ff", cursor: 'pointer' }} />
              </IconButton>
            </Link>
          </Tooltip>
        </div>
      }
      />
      {invoice && invoice.loading === true &&
        <LinearProgress />
      }
      <Divider />
      <CardContent>
        <Container maxWidth="sm">
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel component="legend">Invoice Emails</FormLabel>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', border: '1px solid #ccc', padding: '2px', borderRadius: '5px' }}>
                  {invoiceEmails.map((email, index) => (
                    <Chip key={index} label={email} onDelete={() => handleDelete(email, 'invoice')} sx={{ margin: '2px' }} />
                  ))}
                  <TextField
                    value={inputValue}
                    onChange={(event) => {
                      setInputValue(event.target.value);
                      if (event.target.value && errors.invoiceEmail) {
                        setErrors((prevErrors) => ({ ...prevErrors, invoiceEmail: null }));
                      } else if (event.target.value === '' && invoiceEmails.length > 0 && errors.invoiceEmail) {
                        setErrors((prevErrors) => ({ ...prevErrors, invoiceEmail: null }));
                      }
                    }}
                    onKeyDown={(event) => handleKeyDown(event, 'invoice')}
                    sx={{ flex: 1, border: 'none', '& fieldset': { border: 'none' }, minWidth: '150px', height: '30px', '& .MuiInputBase-input': { height: '30px', padding: '0 0 0 5px', boxSizing: 'border-box' } }}
                    placeholder="Enter email addresses"
                    InputProps={{ disableUnderline: true }}
                  />
                </Box>
                {errors.invoiceEmail && <div style={{ color: 'red' }}>{errors.invoiceEmail}</div>}
              </Grid>
              <Grid item xs={12}>
                <FormLabel component="legend">Invoice CC Emails</FormLabel>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', border: '1px solid #ccc', padding: '2px', borderRadius: '5px' }}>
                  {invoiceCCEmails.map((email, index) => (
                    <Chip key={index} label={email} onDelete={() => handleDelete(email, 'invoiceCC')} sx={{ margin: '2px' }} />
                  ))}
                  <TextField
                    value={inputCCValue}
                    onChange={(event) => {
                      setInputCCValue(event.target.value);
                    }}
                    onKeyDown={(event) => handleKeyDown(event, 'invoiceCC')}
                    sx={{ flex: 1, border: 'none', '& fieldset': { border: 'none' }, minWidth: '150px', height: '30px', '& .MuiInputBase-input': { height: '30px', padding: '0 0 0 5px', boxSizing: 'border-box' } }}
                    placeholder="Enter email addresses"
                    InputProps={{ disableUnderline: true }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <FormLabel component="legend">Prefer your invoice type</FormLabel>
                  <RadioGroup
                    name="invoiceType"
                    value={invoiceType}
                    onChange={handleInvoiceTypeChange}
                  >
                    <FormControlLabel value="standard" control={<Radio />} label="Standard CSV" />
                    <FormControlLabel value="extended" control={<Radio />} label="Extended CSV" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" type="submit">
                  Submit
                </Button>
              </Grid>
            </Grid>
          </form>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption">
                  <span style={{ fontWeight: 'bold' }}>Note:</span> You can enter multiple recipient emails separated by commas.
            </Typography>
          </Box>
        </Container>
      </CardContent>
    </Card>

  </>
  );
}

export default InvoiceSettingsPage;